/**
 * WonderCreditsPage — /wonder-credits
 * Explains the UCWonder Credits program: earn (signup, AI planning, claim on
 * completion, hidden surprises) and redeem (Wonder Perks partner rewards).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, BedDouble, CalendarCheck, Coins, Compass, Gift, MapPin, Plane,
  Sparkles, Ticket, UserPlus, Utensils, Wand2,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { WonderCoin } from '../components/WonderCoin';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = '#1E3F70';
const GOLD = '#E5B025';
const BLUE = '#78aeff';

const Chip = ({ icon: Icon, amount, label }) => (
  <div className="flex items-center gap-3 rounded-xl px-4 py-3 bg-white ring-1 ring-gray-200">
    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full shrink-0" style={{ background: '#E0E8F2' }}>
      <Icon className="w-5 h-5" style={{ color: NAVY }} />
    </span>
    <div>
      <div className="text-sm font-extrabold" style={{ color: NAVY }}><WonderCoin size={15} className="mr-0.5" />+{amount} Wonders</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  </div>
);

export default function WonderCreditsPage() {
  const [cfg, setCfg] = useState(null);
  const authed = !!(localStorage.getItem('user_token') || localStorage.getItem('token'));

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API_BASE}/points/config`).then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  const c = cfg || {};
  return (
    <div className="min-h-screen bg-[#f7fafd]" data-testid="wonder-credits-page">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-14 pb-10 text-center">
        <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] rounded-full px-4 py-1.5" style={{ background: '#FFF7E0', color: '#8a6d1a' }}>
          <Coins className="w-3.5 h-3.5" /> Wonders · UCWonder Perks
        </span>
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-4">
          Welcome to <span style={{ color: NAVY }}>Wonder Perks!</span>
        </h1>
        <p className="text-base md:text-lg text-gray-600 mt-4 max-w-2xl mx-auto" data-testid="wonder-perks-intro">
          Meet <b>Wonders</b> — UCWonder's reward points. Redeem your earned Wonders for exclusive
          exclusive global partner gift cards and lifestyle perks. All redemptions are strictly non-cash
          partner Perks, powered by authorized supply chains.
        </p>
      </section>

      {/* How to earn */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-12 space-y-5">
        <Card className="p-6" data-testid="wonder-credits-step-signup">
          <div className="flex items-start gap-4">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl shrink-0" style={{ background: NAVY }}>
              <UserPlus className="w-5 h-5 text-white" />
            </span>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-gray-900">1 · Create your free account</h2>
              <p className="text-sm text-gray-600 mt-1">
                {c.signup_bonus || 100} Wonders land in your Perks Wallet the moment you join —
                no booking required.
              </p>
            </div>
            <span className="text-xl font-extrabold shrink-0" style={{ color: GOLD }}><WonderCoin size={19} className="mr-1 align-[-3px]" />+{c.signup_bonus || 100}</span>
          </div>
        </Card>

        <Card className="p-6" data-testid="wonder-credits-step-plan">
          <div className="flex items-start gap-4">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl shrink-0" style={{ background: NAVY }}>
              <Wand2 className="w-5 h-5 text-white" />
            </span>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-gray-900">2 · Plan with UCWonder AI</h2>
              <p className="text-sm text-gray-600 mt-1 mb-4">
                Every choice you lock in with our AI trip planner earns Wonders — pick a destination
                on the globe, choose your hotel and flight, add attractions and restaurants.
                Wonders reward your planning activity on UCWonder itself — they're never earned
                from bookings, purchases, or partner links.
              </p>
              <div className="grid sm:grid-cols-2 gap-2.5">
                <Chip icon={BedDouble} amount={c.per_hotel ?? 100} label="per hotel planned" />
                <Chip icon={Plane} amount={c.per_flight ?? 100} label="per flight planned" />
                <Chip icon={Ticket} amount={c.per_activity ?? 50} label="per activity added" />
                <Chip icon={Utensils} amount={c.per_restaurant ?? 50} label="per restaurant picked" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6" data-testid="wonder-credits-step-claim">
          <div className="flex items-start gap-4">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl shrink-0" style={{ background: NAVY }}>
              <CalendarCheck className="w-5 h-5 text-white" />
            </span>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-gray-900">3 · Finish your trip &amp; claim</h2>
              <p className="text-sm text-gray-600 mt-1">
                Your planning Wonders stay <b>pending</b> while you build the trip. The moment you
                complete your plan while signed in, they're claimed automatically and added to your
                wallet. Planned as a guest? Sign in afterwards — your trip and Wonders come with you.
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6" style={{ background: '#FBF6FF' }} data-testid="wonder-credits-step-surprises">
          <div className="flex items-start gap-4">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl shrink-0" style={{ background: '#6b21a8' }}>
              <Gift className="w-5 h-5 text-white" />
            </span>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-gray-900">4 · Psst… surprises are hiding</h2>
              <p className="text-sm text-gray-600 mt-1">
                Hidden one-time Wonder drops are scattered around UCWonder — exploring, sharing and
                discovering can all trigger them. We won't spoil where. <Sparkles className="w-3.5 h-3.5 inline" style={{ color: GOLD }} />
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* Redeem */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        <Card className="p-8 text-center" style={{ background: NAVY }} data-testid="wonder-credits-redeem">
          <Compass className="w-8 h-8 mx-auto mb-3" style={{ color: GOLD }} />
          <h2 className="text-xl font-bold text-white">Redeem in the Perks Center</h2>
          <p className="text-sm text-white/70 mt-2 max-w-xl mx-auto">
            Turn your Wonders into gift cards and lifestyle perks from thousands of partner
            brands worldwide — dining, transport, shopping and more. Redemption is rolling out
            progressively, so early members benefit first.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            {!authed && (
              <Button asChild className="rounded-full font-bold" style={{ background: GOLD, color: NAVY }} data-testid="wonder-credits-cta-signup">
                <Link to="/signup">Create account — get {c.signup_bonus || 100} Wonders</Link>
              </Button>
            )}
            <Button asChild variant="outline" className="rounded-full bg-white font-bold" data-testid="wonder-credits-cta-plan">
              <Link to="/plan">Start planning &amp; earning <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full bg-white font-bold" data-testid="wonder-credits-cta-brands">
              <Link to="/dining">Browse partner brands</Link>
            </Button>
          </div>
        </Card>
        <p className="text-[11px] text-gray-400 text-center mt-4 flex items-center justify-center gap-1.5">
          <MapPin className="w-3 h-3" /> Wonders are UCWonder's loyalty points, earned through activity on UCWonder — never for bookings made with partners. They have no cash value and can only be exchanged for partner Perks.
        </p>
      </section>
    </div>
  );
}
